
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  Link as LinkIcon,
  Subscriptions as SubscriptionsIcon,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import api, { apiService } from '../services/api';
import Layout from '../components/Layout';

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#F7FAFC',
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: 280, // 80px for sidebar + 200px for submenu
}));

interface BillingStats {
  active_subscriptions: number;
  trialing_subscriptions: number;
  monthly_revenue: number;
  annual_revenue: number;
  tier_breakdown: Record<string, number>;
}

interface Subscription {
  id: string;
  stripe_subscription_id: string;
  company_id: string;
  company_name: string;
  plan: string;
  billing_cycle: string;
  status: string;
  total_amount: number;
  current_period_end: string;
  stripe_metadata: any;
}

interface PromoChain {
  id: string;
  name: string;
  description: string;
  chain_definition: any[];
  is_active: boolean;
}

const AdminBillingPage: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<BillingStats | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [promoChains, setPromoChains] = useState<PromoChain[]>([]);
  
  // Determine active view from URL
  const isPromoView = location.pathname.includes('/promos');

  // Modal states
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [newPromo, setNewPromo] = useState({
    name: '',
    description: '',
    steps: [{ 
      step: 1, 
      discount_type: 'percentage', 
      discount_value: 0, 
      duration_months: 1 
    }]
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, subsRes, promosRes] = await Promise.all([
        apiService.getBillingStats(),
        apiService.listSubscriptions(),
        apiService.getPromoChains()
      ]);
      setStats(statsRes as BillingStats);
      setSubscriptions((subsRes as any).subscriptions || []);
      setPromoChains(promosRes as PromoChain[]);
    } catch (err) {
      console.error("Error fetching billing data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePromo = async () => {
    try {
      await apiService.createPromoChain({
        name: newPromo.name,
        description: newPromo.description,
        chain_definition: newPromo.steps
      });
      setPromoModalOpen(false);
      fetchData();
    } catch (err) {
      alert("Error creating promo chain");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'trialing': return 'info';
      case 'past_due': return 'warning';
      case 'canceled': return 'error';
      default: return 'default';
    }
  };

  return (
    <PageContainer>
      <Layout>
        <MainContent>
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" fontWeight="bold">
              {isPromoView ? 'Promo Chains' : 'Subscription Dashboard'}
            </Typography>
            {!isPromoView && (
              <Box>
                <Typography variant="body2" color="textSecondary">
                  PropTrak Billing Overview
                </Typography>
              </Box>
            )}
            {isPromoView && (
              <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => setPromoModalOpen(true)}
              >
                Create Promo Chain
              </Button>
            )}
          </Box>

          {loading && !stats ? (
            <Box display="flex" justifyContent="center" py={10}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Stats Overview */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" variant="subtitle2">Monthly Revenue (MRR)</Typography>
                      <Typography variant="h4" fontWeight="bold">${stats?.monthly_revenue.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" variant="subtitle2">Annual Revenue (ARR)</Typography>
                      <Typography variant="h4" fontWeight="bold">${stats?.annual_revenue.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" variant="subtitle2">Active Subscriptions</Typography>
                      <Typography variant="h4" fontWeight="bold">{stats?.active_subscriptions}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" variant="subtitle2">Trialing</Typography>
                      <Typography variant="h4" fontWeight="bold">{stats?.trialing_subscriptions}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* View Content */}
              <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                {!isPromoView ? (
                  <TableContainer>
                    <Table>
                      <TableHead sx={{ backgroundColor: '#F8FAFC' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Cycle</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Next Billing</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {subscriptions.map((sub) => (
                            <TableRow key={sub.id} hover>
                              <TableCell sx={{ fontWeight: '600' }}>{sub.company_name}</TableCell>
                              <TableCell>{sub.plan}</TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>{sub.billing_cycle}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={sub.status.toUpperCase()} 
                                  color={getStatusColor(sub.status) as any} 
                                  size="small" 
                                  sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2">${(sub.total_amount / 100).toFixed(2)}</Typography>
                                  {sub.status === 'trialing' && (
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.65rem', mt: 0.5 }}>
                                      Post-trial amount
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>{sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : 'N/A'}</TableCell>
                            <TableCell align="right">
                              <Button 
                                component={Link} 
                                to={`/companies/${sub.company_id}`}
                                variant="outlined" 
                                size="small" 
                                startIcon={<LinkIcon />}
                              >
                                View Company
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box p={3}>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      Daisy Chains allow you to set phased discounts. When one coupon expires, the next one in the chain is automatically applied.
                    </Alert>
                    <TableContainer>
                      <Table>
                        <TableHead sx={{ backgroundColor: '#F8FAFC' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Chain Steps</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {promoChains.map((chain) => (
                            <TableRow key={chain.id} hover>
                              <TableCell sx={{ fontWeight: '600' }}>{chain.name}</TableCell>
                              <TableCell>{chain.description}</TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {chain.chain_definition.map((step, idx) => (
                                <Chip 
                                  key={idx} 
                                  label={`${step.discount_type === 'percentage' ? step.discount_value + '%' : '$' + step.discount_value} (${step.duration_months || '∞'} mo)`} 
                                  size="small" 
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </TableCell>
                              <TableCell>
                                {chain.is_active ? (
                                  <Chip label="Active" color="success" size="small" variant="filled" />
                                ) : (
                                  <Chip label="Inactive" size="small" variant="filled" />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton 
                                  color="error" 
                                  size="small"
                                  onClick={async () => {
                                    if(window.confirm("Disable this promo chain?")) {
                                      await apiService.deletePromoChain(chain.id);
                                      fetchData();
                                    }
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Paper>
            </>
          )}
        </MainContent>
      </Layout>

      {/* Create Promo Modal */}
      <Dialog open={promoModalOpen} onClose={() => setPromoModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Create Phased Promo Chain</DialogTitle>
        <DialogContent>
          <Box pt={1} display="flex" flexDirection="column" gap={3}>
            <TextField 
              label="Chain Name" 
              placeholder="e.g., New Year Launch Special"
              fullWidth 
              value={newPromo.name}
              onChange={(e) => setNewPromo({...newPromo, name: e.target.value})}
            />
            <TextField 
              label="Description" 
              placeholder="Internal notes about this promo"
              fullWidth 
              multiline 
              rows={2}
              value={newPromo.description}
              onChange={(e) => setNewPromo({...newPromo, description: e.target.value})}
            />
            
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Phases (Sequence)</Typography>
              <Divider sx={{ mb: 2 }} />
              {newPromo.steps.map((step: any, idx) => (
                <Box key={idx} sx={{ mb: 4, p: 2, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2" color="primary">Phase {idx + 1}</Typography>
                    {newPromo.steps.length > 1 && (
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => {
                          const steps = newPromo.steps.filter((_, i) => i !== idx);
                          setNewPromo({...newPromo, steps});
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <RadioGroup
                        row
                        value={step.discount_type}
                        onChange={(e) => {
                          const steps = [...newPromo.steps];
                          steps[idx].discount_type = e.target.value;
                          setNewPromo({...newPromo, steps});
                        }}
                      >
                        <FormControlLabel value="percentage" control={<Radio size="small" />} label="%" />
                        <FormControlLabel value="amount" control={<Radio size="small" />} label="$" />
                      </RadioGroup>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField 
                        label={step.discount_type === 'percentage' ? "Discount %" : "Discount $"}
                        size="small" 
                        fullWidth
                        type="number"
                        value={step.discount_value}
                        InputProps={{
                          startAdornment: step.discount_type === 'amount' ? <InputAdornment position="start">$</InputAdornment> : null,
                          endAdornment: step.discount_type === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : null,
                        }}
                        onChange={(e) => {
                          const steps = [...newPromo.steps];
                          steps[idx].discount_value = parseFloat(e.target.value) || 0;
                          setNewPromo({...newPromo, steps});
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TextField 
                        label="Duration (Months)" 
                        placeholder="e.g., 3 (0 or empty for forever)"
                        size="small" 
                        fullWidth
                        type="number"
                        value={step.duration_months}
                        onChange={(e) => {
                          const steps = [...newPromo.steps];
                          steps[idx].duration_months = parseInt(e.target.value) || 0;
                          setNewPromo({...newPromo, steps});
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
              <Button 
                startIcon={<AddIcon />} 
                variant="outlined"
                fullWidth
                onClick={() => setNewPromo({
                  ...newPromo, 
                  steps: [...newPromo.steps, { 
                    step: newPromo.steps.length + 1, 
                    discount_type: 'percentage', 
                    discount_value: 0, 
                    duration_months: 1 
                  }]
                })}
              >
                Add Next Phase
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setPromoModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePromo} disabled={!newPromo.name || newPromo.steps.length === 0}>
            Create Chain
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default AdminBillingPage;
